// Modal dodawania komentarza i/lub załącznika do pozycji oferty.

import React, { type FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, CircularProgress, FormControlLabel, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useQueryClient } from 'react-query';

import { WiseModal } from 'components/wise';
import { usePostSalesCrmOfferPositionComment } from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/AddPositionCommentModal.module.scss';

interface IAddPositionCommentModalProps {
  open: boolean;
  clientId: number;
  offerId: string;
  positionId: number;
  onClose: () => void;
}

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AddPositionCommentModal: FC<IAddPositionCommentModalProps> = ({
  open,
  clientId,
  offerId,
  positionId,
  onClose
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [comment, setComment] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: postComment, isLoading } = usePostSalesCrmOfferPositionComment(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'sales-crm',
          'client',
          clientId,
          'offer',
          offerId,
          'position',
          positionId,
          'comments'
        ]);
        resetState();
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error?.message || t('Nie udało się zapisać komentarza.'));
      }
    }
  );

  const resetState = () => {
    setComment('');
    setIsVisible(false);
    setFileBase64(null);
    setFileName(null);
    setErrorMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await readFileAsBase64(file);
      setFileBase64(base64);
      setFileName(file.name);
      setErrorMessage(null);
    } catch {
      setErrorMessage(t('Nie udało się odczytać pliku.'));
    }
  };

  const handleRemoveFile = () => {
    setFileBase64(null);
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = () => {
    const trimmed = comment.trim();
    if (!trimmed && !fileBase64) {
      setErrorMessage(t('Treść komentarza lub załącznik są wymagane.'));
      return;
    }
    setErrorMessage(null);
    postComment({
      comment: trimmed,
      isVisibleForClient: isVisible,
      fileBase64: fileBase64,
      fileName: fileName
    });
  };

  const canSubmit = !isLoading && (comment.trim().length > 0 || !!fileBase64);

  return (
    <WiseModal
      open={open}
      onClose={handleClose}
      title={t('Dodaj komentarz / załącznik')}
      maxWidth={520}>
      <div className={styles.body}>
        <TextField
          label={t('Treść komentarza')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          autoFocus
        />

        <div className={styles.fileRow}>
          <input
            ref={fileInputRef}
            type="file"
            className={styles.fileInputHidden}
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outlined"
            onClick={handlePickFile}
            startIcon={<AttachFileIcon />}
            sx={{
              borderColor: '#6b54ff',
              color: '#6b54ff',
              '&:hover': { borderColor: '#5a47e0', backgroundColor: 'rgba(107,84,255,0.04)' }
            }}>
            {t('Wybierz załącznik (opcjonalnie)')}
          </Button>

          {fileName && (
            <div className={styles.fileChip}>
              <span className={styles.fileChipName}>{fileName}</span>
              <button
                type="button"
                className={styles.fileChipRemove}
                onClick={handleRemoveFile}
                aria-label={t('Usuń załącznik')}>
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            </div>
          )}
        </div>

        <FormControlLabel
          control={
            <Checkbox
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
              sx={{
                color: '#6b54ff',
                '&.Mui-checked': { color: '#6b54ff' }
              }}
            />
          }
          label={t('Widoczny dla klienta')}
        />

        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.actions}>
          <Button onClick={handleClose} variant="text">
            {t('Anuluj')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isLoading ? <CircularProgress size={18} /> : t('Zapisz')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default AddPositionCommentModal;
